/**
 * Application entry point
 * Initializes React and mounts the App component
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App";
import { initSentry } from "./lib/sentry";
import { isLocatorHost } from "./utils/locatorHost";
import { captureInstallPrompt } from "./utils/installPrompt";

// Initialize Sentry for error tracking
initSentry();

// Register the notifications service worker (Web Push). Best-effort; failure here
// must never block app startup. Push opt-in itself is gated behind a user action.
// Not on the public locator hosts (qa-locator / locator.uslawshield.com): they serve only the locator page, and a
// push worker registered on that origin would serve no purpose (nginx answers /sw.js with 404 there).
if ("serviceWorker" in navigator && !isLocatorHost(window.location.hostname)) {
  window.addEventListener("load", () => {
    navigator.serviceWorker.register("/sw.js").catch((err) => {
      console.warn("[SW] registration failed", err);
    });
  });
}

// Hold Chrome's "install this app" event for the MSR Add-to-Home-Screen banner (it can fire before React mounts).
if (!isLocatorHost(window.location.hostname)) {
  captureInstallPrompt();
}

// Get root element
const rootElement = document.getElementById("root");

if (!rootElement) {
  throw new Error("Root element not found");
}

const strictModePreference = String(
  import.meta.env.VITE_ENABLE_STRICT_MODE ?? "true",
)
  .toLowerCase()
  .trim();
const shouldEnableStrictMode =
  strictModePreference !== "false" && strictModePreference !== "0";

if (!shouldEnableStrictMode) {
  console.warn("[App] React StrictMode disabled via VITE_ENABLE_STRICT_MODE");
}

// Create root and render app
createRoot(rootElement).render(
  shouldEnableStrictMode ? (
    <StrictMode>
      <App />
    </StrictMode>
  ) : (
    <App />
  ),
);
